/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook used to retrieve the Theme Manager Context.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to get access to react base hooks inside custom hook.
 */
import { useEffect } from 'react';

/**
 * Allows for the dispatching of actions to the redux store.
 */
import { useDispatch } from 'react-redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Enables the use of grade data loaded from the ServiceStack API.
 */
import { GradeSelectors, useGradeSelector, GradeActions } from 'src/store/reducers/grade';

/**
 * Typings
 */
import { IRequestState } from 'src/store/reducers';

/**
 * Typings
 */
import * as Dtos from '../api/dtos';

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const useGrade = (
    gradeId: number
): [
    Dtos.Version | null,
    Dtos.Category | null,
    Dtos.Term | null,
    Dtos.Term | null,
    Dtos.Grade | null,
    IRequestState
] => {
    const version = useGradeSelector(state => GradeSelectors.selectGradeVersion(state, gradeId));
    const category = useGradeSelector(state => GradeSelectors.selectGradeCategory(state, gradeId));
    const term = useGradeSelector(state => GradeSelectors.selectGradeTerm(state, gradeId));
    const subterm = useGradeSelector(state => GradeSelectors.selectGradeSubterm(state, gradeId));
    const grade = useGradeSelector(state => GradeSelectors.selectGrade(state, gradeId));
    const loadState = useGradeSelector(state => GradeSelectors.selectGradeLoadState(state, gradeId));

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(GradeActions.loadById(gradeId));

        return () => {
            dispatch(GradeActions.clear(gradeId));
        }
    }, [gradeId, dispatch]);

    return [
        version,
        category,
        term,
        subterm,
        grade,
        loadState
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Exports - Default
 * ---------------------------------------------------------------------------------
 */

export default useGrade;


// WEBPACK FOOTER //
// ./src/hooks/useGrade.ts